/**
 * Symbol Converter Utility
 *
 * Converts between exchange-specific symbol formats and CCXT unified format.
 * Optimized for the three supported quote currencies: USDT, BTC, KRW
 *
 * Exchange-specific formats:
 * - Binance Spot/Futures: "BTCUSDT" (base + quote concatenated)
 * - Upbit:                "KRW-BTC", "USDT-ETH", "BTC-XRP" (quote-base with dash)
 * - Bithumb:              "BTC_KRW" (base_quote with underscore)
 *
 * CCXT unified format:
 * - Spot:    "BTC/USDT", "BTC/KRW"
 * - Futures: "BTC/USDT:USDT" (perpetual)
 *
 * Supported quotes: USDT, BTC, KRW
 */

import { Exchange } from '../types/futuresSpot';

/** Supported quote currencies */
type SupportedQuote = 'USDT' | 'BTC' | 'KRW';

/**
 * Convert exchange-specific symbol to CCXT unified format
 * Uses byte-level checks for efficiency (per CLAUDE.md pattern)
 */
export function toCcxtSymbol(exchangeSymbol: string, exchange: Exchange): string {
  switch (exchange) {
    case Exchange.Binance:
    case Exchange.BinanceFutures: {
      // Binance format: "{BASE}{QUOTE}" e.g., "BTCUSDT", "ETHBTC"
      // Check last char: 'T' = USDT (4 chars), 'C' = BTC (3 chars)
      const len = exchangeSymbol.length;
      const lastChar = exchangeSymbol[len - 1];

      if (lastChar === 'T' && len > 4) {
        // USDT (4 chars)
        const base = exchangeSymbol.slice(0, len - 4);
        return exchange === Exchange.BinanceFutures
          ? `${base}/USDT:USDT`
          : `${base}/USDT`;
      }
      if (lastChar === 'C' && len > 3) {
        // BTC (3 chars)
        const base = exchangeSymbol.slice(0, len - 3);
        return `${base}/BTC`;
      }
      // Fallback
      return exchangeSymbol;
    }

    case Exchange.Upbit: {
      // Upbit format: "{QUOTE}-{BASE}" e.g., "KRW-BTC", "USDT-ETH", "BTC-XRP"
      const dashIndex = exchangeSymbol.indexOf('-');
      if (dashIndex === -1) return exchangeSymbol;

      const quote = exchangeSymbol.slice(0, dashIndex);
      const base = exchangeSymbol.slice(dashIndex + 1);
      return `${base}/${quote}`;
    }

    case Exchange.Bithumb: {
      // Standard: "{BASE}_{QUOTE}" e.g., "BTC_KRW"
      // API may also return Upbit-style: "{QUOTE}-{BASE}"
      if (exchangeSymbol.includes('_')) {
        return exchangeSymbol.replace('_', '/');
      }
      if (exchangeSymbol.includes('-')) {
        const dashIndex = exchangeSymbol.indexOf('-');
        const quote = exchangeSymbol.slice(0, dashIndex);
        const base = exchangeSymbol.slice(dashIndex + 1);
        return `${base}/${quote}`;
      }
      return exchangeSymbol;
    }

    default:
      return exchangeSymbol;
  }
}

/**
 * Convert CCXT unified symbol to exchange-specific format
 */
export function toExchangeSymbol(ccxtSymbol: string, exchange: Exchange): string {
  // Parse CCXT format: "BTC/USDT" or "BTC/USDT:USDT"
  const slashIndex = ccxtSymbol.indexOf('/');
  if (slashIndex === -1) return ccxtSymbol;

  const colonIndex = ccxtSymbol.indexOf(':');
  const base = ccxtSymbol.slice(0, slashIndex);
  const quote = colonIndex > 0
    ? ccxtSymbol.slice(slashIndex + 1, colonIndex)
    : ccxtSymbol.slice(slashIndex + 1);

  switch (exchange) {
    case Exchange.Binance:
    case Exchange.BinanceFutures:
      return `${base}${quote}`;

    case Exchange.Upbit:
      return `${quote}-${base}`;

    case Exchange.Bithumb:
      return `${base}_${quote}`;

    default:
      return ccxtSymbol;
  }
}

/**
 * Extract base and quote from exchange-specific symbol
 * Optimized for supported quotes: USDT (ends with T), BTC (ends with C), KRW
 */
export function parseExchangeSymbol(
  exchangeSymbol: string,
  exchange: Exchange
): { base: string; quote: SupportedQuote } | null {
  switch (exchange) {
    case Exchange.Binance:
    case Exchange.BinanceFutures: {
      const len = exchangeSymbol.length;
      const lastChar = exchangeSymbol[len - 1];

      if (lastChar === 'T' && len > 4) {
        return { base: exchangeSymbol.slice(0, len - 4), quote: 'USDT' };
      }
      if (lastChar === 'C' && len > 3) {
        return { base: exchangeSymbol.slice(0, len - 3), quote: 'BTC' };
      }
      return null;
    }

    case Exchange.Upbit:
    case Exchange.Bithumb: {
      // Both use dash or underscore separators
      const dashIndex = exchangeSymbol.indexOf('-');
      if (dashIndex > 0) {
        // Upbit-style: "{QUOTE}-{BASE}"
        const quote = exchangeSymbol.slice(0, dashIndex) as SupportedQuote;
        const base = exchangeSymbol.slice(dashIndex + 1);
        return { base, quote };
      }
      const underscoreIndex = exchangeSymbol.indexOf('_');
      if (underscoreIndex > 0) {
        // Bithumb-style: "{BASE}_{QUOTE}"
        const base = exchangeSymbol.slice(0, underscoreIndex);
        const quote = exchangeSymbol.slice(underscoreIndex + 1) as SupportedQuote;
        return { base, quote };
      }
      return null;
    }

    default:
      return null;
  }
}

/**
 * Check if a symbol is in CCXT unified format
 */
export function isCcxtFormat(symbol: string): boolean {
  return symbol.includes('/');
}

/**
 * Get the base asset from any symbol format
 */
export function getBaseAsset(symbol: string, exchange: Exchange): string | null {
  return parseExchangeSymbol(symbol, exchange)?.base ?? null;
}

/**
 * Get the quote asset from any symbol format
 */
export function getQuoteAsset(symbol: string, exchange: Exchange): string | null {
  return parseExchangeSymbol(symbol, exchange)?.quote ?? null;
}
